import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { CountryLabel } from "@/components/country-label";
import { MapView } from "@/components/map-view";
import {
  videosByCountryQuery,
  creatorsListQuery,
  primaryLocation,
  type VideoWithLocations,
  videoParam,
} from "@/lib/queries";
import {
  cleanTitle,
  formatCompact,
  formatDate,
  formatDuration,
  translateCountry,
} from "@/lib/format";
import { ArrowUp, ArrowDown, Youtube, Eye, CalendarDays, Users } from "lucide-react";
import { safeLoadAll } from "@/lib/ssr-safe";
import "@/i18n";

type SortKey = "published_at" | "view_count" | "duration_seconds" | "title" | "location" | "channel";
type SortDir = "asc" | "desc";

export const Route = createFileRoute("/country/$code")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const code = (params.code || "").toUpperCase();
    const name = translateCountry(code, code, "es");
    const title = `Vídeos en ${name} · Youtubers Map`;
    const description = `Todos los vídeos de youtubers viajeros grabados en ${name}: mapa de localizaciones, canales, vídeos más vistos y estadísticas.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  loader: async ({ context, params }) => {
    await safeLoadAll([
      [
        "country:videos",
        context.queryClient.ensureQueryData(videosByCountryQuery((params.code || "").toUpperCase())),
      ],
      ["country:creators", context.queryClient.ensureQueryData(creatorsListQuery)],
    ]);
  },
  component: CountryPage,
});

function CountryPage() {
  return (
    <div className="min-h-screen">
      <SiteNav />
      <Suspense fallback={<div className="p-24 text-center text-ink/40">…</div>}>
        <CountryContent />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function locKey(l: NonNullable<ReturnType<typeof primaryLocation>>) {
  return [l.city, l.region, l.name].filter(Boolean).join(" · ") || "—";
}

function CountryContent() {
  const { t, i18n } = useTranslation();
  const { code: rawCode } = Route.useParams();
  const code = rawCode.toUpperCase();

  const { data: videos } = useSuspenseQuery(videosByCountryQuery(code));
  const { data: creators } = useSuspenseQuery(creatorsListQuery);

  const countryName = useMemo(
    () => translateCountry(code, code, i18n.language),
    [code, i18n.language],
  );
  usePageMeta(t("meta.countryTitle", { name: countryName }), t("meta.countryDesc", { name: countryName }));

  const rows = useMemo(
    () => videos.filter((v) => primaryLocation(v)?.country_code === code),
    [videos, code],
  );

  const creatorByChannel = useMemo(() => {
    const m = new Map<string, { name: string; slug: string; avatar: string | null }>();
    for (const c of creators)
      for (const ch of c.channels)
        m.set(ch.id, { name: c.name, slug: c.slug, avatar: ch.avatar_url ?? c.avatar_url });
    return m;
  }, [creators]);

  const stats = useMemo(() => {
    const people = new Set<string>();
    const locations = new Set<string>();
    let views = 0;
    for (const v of rows) {
      const c = creatorByChannel.get(v.youtuber_id);
      people.add(c?.slug ?? v.youtuber_id);
      const l = primaryLocation(v);
      if (l) locations.add(locKey(l));
      views += v.view_count ?? 0;
    }
    return { videos: rows.length, youtubers: people.size, locations: locations.size, views };
  }, [rows, creatorByChannel]);

  const highlights = useMemo(() => {
    const perChannel = new Map<string, { count: number; video: VideoWithLocations }>();
    let mostViewed: VideoWithLocations | null = null;
    let latest: VideoWithLocations | null = null;
    for (const v of rows) {
      const e = perChannel.get(v.youtuber_id);
      perChannel.set(v.youtuber_id, { count: (e?.count ?? 0) + 1, video: v });
      if (!mostViewed || (v.view_count ?? 0) > (mostViewed.view_count ?? 0)) mostViewed = v;
      if (
        !latest ||
        new Date(v.published_at ?? 0).getTime() > new Date(latest.published_at ?? 0).getTime()
      )
        latest = v;
    }
    const tops: Array<{ channelId: string; count: number }> = [];
    perChannel.forEach((val, key) => tops.push({ channelId: key, count: val.count }));
    tops.sort((a, b) => b.count - a.count);
    const top = tops[0] ?? null;
    return { top, mostViewed: mostViewed as VideoWithLocations | null, latest: latest as VideoWithLocations | null };
  }, [rows]);

  const locations = useMemo(() => {
    const m = new Map<
      string,
      { key: string; city: string | null; region: string | null; name: string | null; videos: number; views: number }
    >();
    for (const v of rows) {
      const l = primaryLocation(v);
      if (!l) continue;
      const key = locKey(l);
      const e =
        m.get(key) ?? { key, city: l.city, region: l.region, name: l.name, videos: 0, views: 0 };
      e.videos += 1;
      e.views += v.view_count ?? 0;
      m.set(key, e);
    }
    return Array.from(m.values()).sort((a, b) => b.videos - a.videos);
  }, [rows]);

  const pins = useMemo(
    () =>
      rows.flatMap((v) => {
        const l = primaryLocation(v);
        if (!l || l.latitude == null || l.longitude == null) return [];
        return [{
          id: v.id,
          lat: Number(l.latitude),
          lng: Number(l.longitude),
          label: cleanTitle(v.title),
          subtitle: [l.city, countryName].filter(Boolean).join(", "),
          href: `/v/${videoParam(v)}`,
          thumbnail: v.thumbnail_url,
          countryCode: l.country_code,
        }];
      }),
    [rows, countryName],
  );

  const [sortKey, setSortKey] = useState<SortKey>("published_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...rows];
    arr.sort((a, b) => {
      const la = primaryLocation(a);
      const lb = primaryLocation(b);
      switch (sortKey) {
        case "published_at":
          return dir * (new Date(a.published_at ?? 0).getTime() - new Date(b.published_at ?? 0).getTime());
        case "view_count":
          return dir * ((a.view_count ?? 0) - (b.view_count ?? 0));
        case "duration_seconds":
          return dir * ((a.duration_seconds ?? 0) - (b.duration_seconds ?? 0));
        case "title":
          return dir * a.title.localeCompare(b.title);
        case "location":
          return dir * (la ? locKey(la) : "").localeCompare(lb ? locKey(lb) : "");
        case "channel":
          return dir * (a.youtubers?.name ?? "").localeCompare(b.youtubers?.name ?? "");
      }
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "title" || key === "location" || key === "channel" ? "asc" : "desc");
    }
  };

  const SortHead = ({ k, label, className = "" }: { k: SortKey; label: string; className?: string }) => (
    <th className={`text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 ${className}`}>
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-ink">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );

  const topChannel = highlights.top ? creatorByChannel.get(highlights.top.channelId) : null;

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-4">
        <Link to="/countries" className="text-mono-caps text-amber hover:text-amber-deep">
          ← {t("countries.title")}
        </Link>
        <h1 className="text-3xl md:text-4xl mt-2 mb-2 flex items-center gap-3">
          <CountryLabel code={code} name={countryName} showName={false} size={40} />
          {t("country.title", { country: countryName })}
        </h1>
        <p className="text-sm text-ink/60 font-mono">
          {t("country.stats", {
            videos: stats.videos,
            youtubers: stats.youtubers,
            locations: stats.locations,
          })}
          {" · "}
          {formatCompact(stats.views)} {t("common.views").toLowerCase()}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="ring-hairline bg-paper overflow-hidden">
          {pins.length > 0 ? (
            <MapView pins={pins} mode="points" className="h-[420px] w-full" pinSize={8} />
          ) : (
            <div className="h-[420px] grid place-items-center text-ink/40 text-sm">
              {t("common.noLocations")}
            </div>
          )}
        </div>
        <div className="grid gap-3 content-start">
          {topChannel && highlights.top && (
            <HighlightCard
              kicker={t("country.topChannel")}
              icon={<Users className="size-3.5" />}
              to={`/y/${topChannel.slug}`}
              avatar={topChannel.avatar}
              title={topChannel.name}
              meta={t("home.videosCount", { count: highlights.top.count })}
            />
          )}
          {highlights.mostViewed && (
            <HighlightCard
              kicker={t("country.mostViewed")}
              icon={<Eye className="size-3.5" />}
              to={`/v/${videoParam(highlights.mostViewed)}`}
              avatar={highlights.mostViewed.thumbnail_url}
              wide
              title={cleanTitle(highlights.mostViewed.title)}
              meta={`${formatCompact(highlights.mostViewed.view_count)} ${t("common.views").toLowerCase()}`}
            />
          )}
          {highlights.latest && (
            <HighlightCard
              kicker={t("country.latest")}
              icon={<CalendarDays className="size-3.5" />}
              to={`/v/${videoParam(highlights.latest)}`}
              avatar={highlights.latest.thumbnail_url}
              wide
              title={cleanTitle(highlights.latest.title)}
              meta={formatDate(highlights.latest.published_at, i18n.language)}
            />
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-serif text-xl">{t("common.videos")}</h2>
          <label className="text-xs font-mono text-ink/60">
            {t("common.perPage")}:{" "}
            <select
              aria-label={t("common.perPage")}
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="bg-paper ring-hairline px-2 py-1 cursor-pointer"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10 bg-secondary/40">
              <tr>
                <th className="w-16 px-3 py-2" />
                <SortHead k="title" label={t("common.title")} />
                <SortHead k="channel" label={t("common.channel")} className="w-48" />
                <SortHead k="location" label={t("common.locations")} className="w-44" />
                <SortHead k="published_at" label={t("common.date")} className="w-28" />
                <SortHead k="view_count" label={t("common.views")} className="w-24" />
                <SortHead k="duration_seconds" label={t("common.duration")} className="w-24" />
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8">
              {pageItems.map((v) => {
                const l = primaryLocation(v);
                const person = creatorByChannel.get(v.youtuber_id);
                return (
                  <tr key={v.id} className="hover:bg-secondary/40 transition-colors">
                    <td className="px-3 py-1.5">
                      <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="block w-16 aspect-video bg-secondary overflow-hidden">
                        {v.thumbnail_url && (
                          <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        )}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5">
                      <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="font-serif hover:text-amber line-clamp-1">
                        {cleanTitle(v.title)}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {person ? (
                        <Link
                          to="/y/$handle"
                          params={{ handle: person.slug }}
                          className="inline-flex items-center gap-2 group"
                        >
                          <ChannelAvatar src={person.avatar} name={person.name} />
                          <span className="font-medium group-hover:text-amber truncate max-w-[9rem]">
                            {person.name}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-ink/50">{v.youtubers?.name ?? "—"}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-ink/70 whitespace-nowrap truncate max-w-[11rem]">
                      {l ? locKey(l) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap">
                      {formatDate(v.published_at, i18n.language)}
                    </td>
                    <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                      {formatCompact(v.view_count)}
                    </td>
                    <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                      {v.duration_seconds != null ? formatDuration(v.duration_seconds) : "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      <a
                        href={`https://youtube.com/watch?v=${v.youtube_video_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-ink/40 hover:text-amber inline-flex"
                        title={t("common.openInYoutube")}
                      >
                        <Youtube className="size-4" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs font-mono">
            <button
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="ring-hairline px-3 py-1.5 bg-paper hover:bg-secondary disabled:opacity-40"
            >
              ← {t("common.prev")}
            </button>
            <span className="text-ink/60 px-2">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="ring-hairline px-3 py-1.5 bg-paper hover:bg-secondary disabled:opacity-40"
            >
              {t("common.next")} →
            </button>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <h2 className="font-serif text-xl mb-3">
          {t("country.locationsTitle", { country: countryName })}
        </h2>
        <div className="ring-hairline bg-paper overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10 bg-secondary/40">
              <tr>
                <th className="text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2">
                  {t("common.locations")}
                </th>
                <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 w-28">
                  {t("common.videos")}
                </th>
                <th className="text-right font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 w-28">
                  {t("common.views")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/8">
              {locations.map((l) => (
                <tr key={l.key} className="hover:bg-secondary/40 transition-colors">
                  <td className="px-3 py-1.5">
                    <Link
                      to="/videos"
                      search={{ q: l.city || l.name || l.key, country: code, channel: "", creator: "", year: "", page: 1, perPage: 50 }}
                      className="font-serif hover:text-amber"
                    >
                      {l.key}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs text-ink/70">{l.videos}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs text-ink/60">
                    {formatCompact(l.views)}
                  </td>
                </tr>
              ))}
              {locations.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-ink/40">
                    {t("common.noLocations")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function ChannelAvatar({ src, name }: { src: string | null | undefined; name: string }) {
  if (!src) {
    return (
      <span className="size-6 rounded-full bg-secondary ring-1 ring-ink/10 grid place-items-center text-[10px] font-mono text-ink/50 flex-shrink-0">
        {name.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      className="size-6 rounded-full object-cover ring-1 ring-ink/10 flex-shrink-0"
    />
  );
}

function HighlightCard({
  kicker,
  icon,
  to,
  avatar,
  title,
  meta,
  wide = false,
}: {
  kicker: string;
  icon: React.ReactNode;
  to: string;
  avatar: string | null | undefined;
  title: string;
  meta: string;
  wide?: boolean;
}) {
  return (
    <Link to={to} className="ring-hairline bg-paper p-3 block hover:ring-amber transition-all group">
      <p className="text-mono-caps text-ink/50 mb-2 inline-flex items-center gap-1.5">
        {icon}
        {kicker}
      </p>
      <div className="flex items-center gap-3">
        {avatar ? (
          <img
            src={avatar}
            alt=""
            loading="lazy"
            className={`${wide ? "w-20 aspect-video rounded-none" : "size-10 rounded-full"} object-cover ring-1 ring-ink/10 flex-shrink-0`}
          />
        ) : null}
        <div className="min-w-0">
          <p className="font-serif text-sm line-clamp-2 group-hover:text-amber">{title}</p>
          <p className="text-[11px] font-mono text-ink/50 mt-0.5">{meta}</p>
        </div>
      </div>
    </Link>
  );
}
